/**
 * tableInteractionLogic.ts - The pure table transforms and layout rules behind the table's interactions.
 */

import {range} from '@benchling/js-utils';
import produce from 'immer';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {parseTextWithLocale} from 'notebook/mediocre/clipboard/parseTextWithLocale';
import {Cell} from 'notebook/tables/constants/CellConstants';
import * as RowConstants from 'notebook/tables/constants/RowConstants';
import {COLUMN_FIELD_DEFAULTS} from 'notebook/tables/constants/TableColumnConstants';
import * as TableConstants from 'notebook/tables/constants/TableConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {UpsertCellsTableOp} from 'notebook/tables/excess/ops/TableOpConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {notifyTextHasBeenLocalized} from 'util/LocaleAwareParseText';
import {normalizeLineEndings} from 'util/normalizeLineEndings';
import {Optional} from 'util/Types';

export interface ExpandedToCellTable {
  table: TableConstants.Table;
  tableOp: UpsertCellsTableOp;
  /** The (empty) cell now at the expanded-to index. */
  cell: Cell;
}

/**
 * Grows `table` with empty rows and columns until it contains `index`. New rows copy the precision
 * of the last row's cells, and new columns take the column field defaults.
 */
export function computeExpandedToCellTable(
  table: TableConstants.Table,
  index: TableIndex
): ExpandedToCellTable {
  assert(!TableUtil.hasLockedDims(table), `Prevented unexpected table expansion with locked dimensions`);
  const [i, j] = index;

  const newColCount = Math.max(0, j + 1 - table.widths.length);
  const newCols = Array(newColCount).fill(TableUtil.makeEmptyCell());
  let newTable = produce(table.table, (draftRows) => {
    for (const draftRow of draftRows) {
      draftRow.push(...newCols);
    }
  });
  const newRowsCount = Math.max(0, i + 1 - table.table.length);
  const lastRow: Array<Cell> = newTable[newTable.length - 1];
  const newRow: Array<Cell> = lastRow.map((cell: Cell) =>
    TableUtil.makeEmptyCell(cell.precision != null ? {precision: cell.precision} : {})
  );
  newTable = produce(newTable, (draftTable) => {
    for (let k = 0; k < newRowsCount; k++) {
      draftTable.push(newRow);
    }
  });
  const newFullTable = produce(table, (draftTable) => {
    draftTable.table = newTable;
    for (const [key, value] of Object.entries(COLUMN_FIELD_DEFAULTS)) {
      (draftTable[key as keyof TableConstants.TableData] as Array<unknown>).push(
        ...Array(newColCount).fill(value)
      );
    }
  });

  const tableOp: UpsertCellsTableOp = {type: 'upsert-cells', cells: []};
  if (newColCount > 0) {
    tableOp.newColIndexes = range(table.table[0].length, newFullTable.table[0].length - 1);
  }
  if (newRowsCount > 0) {
    tableOp.newRowIndexes = range(table.table.length, newFullTable.table.length - 1);
  }
  return {table: newFullTable, tableOp, cell: newTable[i][j]};
}

/**
 * Parses tab-separated clipboard text into a rectangular grid of cells, ignoring trailing newlines and
 * padding short rows with empty cells. With locale-aware parsing enabled, numbers are re-parsed for
 * `tableType`'s locale and the user is told when any text changed.
 */
export function parseRawDataToCells(data: string, tableType: Optional<string>): RowConstants.Cells {
  let text = normalizeLineEndings(data);
  while (text.endsWith('\n')) {
    text = text.slice(0, -1);
  }

  const localeAware = BenchlingConfig.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE;
  let didAnyCellsGetParsed = false;
  const rows = text.split('\n').map((line) =>
    line.split('\t').map((cellText) => {
      if (localeAware) {
        const localeParsedText = parseTextWithLocale(cellText, tableType);
        if (localeParsedText !== cellText) {
          didAnyCellsGetParsed = true;
          return TableUtil.makeCell(localeParsedText);
        }
      }
      return TableUtil.makeCell(cellText);
    })
  );
  if (localeAware && didAnyCellsGetParsed) {
    notifyTextHasBeenLocalized();
  }

  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  return rows.map((row) =>
    row.concat(Array.from({length: columnCount - row.length}, () => TableUtil.makeEmptyCell()))
  );
}

/** Whether the footer and horizontal scrollbar stick to the bottom of the viewport. */
export function computeIsBottomContainerSticky({
  isFullHeight,
  viewer,
  showFooter,
}: {
  isFullHeight: boolean;
  viewer: boolean;
  showFooter: boolean;
}): boolean {
  // The persistent horizontal scrollbar shows whenever the table is not a viewer.
  return isFullHeight && (!viewer || showFooter);
}
